import featureFlagModule, { FeatureFlagKeys } from '@modules/FeatureFlagModule';
import getServiceQueueById from '../api/getQueueById';
import ServiceQueue from '../types/ServiceQueue';
import getQueue from './getQueue';
import { logException } from './log';
import refreshQueues from './refreshQueues';
import { serviceState } from './state';

const inFlight = new Map<string, Promise<ServiceQueue | null>>();

async function fetchAndUpsert(queueId: string): Promise<ServiceQueue | null> {
  const fresh = await getServiceQueueById(queueId);
  if (!fresh) return null;

  const existing = getQueue(queueId);
  const merged: ServiceQueue =
    existing && (existing.status || existing.sessions?.length)
      ? {
          ...fresh,
          ...(existing.status && { status: existing.status }),
          ...(existing.sessions?.length && { sessions: existing.sessions }),
        }
      : fresh;

  const index = serviceState.queues.findIndex((q) => q.id === queueId);
  const next = [...serviceState.queues];
  if (index >= 0) {
    next[index] = merged;
  } else {
    next.push(merged);
  }
  // reassign the full array so state.ts subscribe rebuilds queueById
  serviceState.queues = next;

  return merged;
}

/**
 * Fetch one queue by id and upsert it into `serviceState.queues`, preserving
 * any existing live `status` and `sessions` already in state. Concurrent calls
 * for the same `queueId` share a single network request via an in-flight
 * dedup map. Returns null when the queue does not exist on the server.
 */
export default function refreshQueue(
  queueId: string,
): Promise<ServiceQueue | null> {
  const pending = inFlight.get(queueId);
  if (pending) return pending;

  const p = fetchAndUpsert(queueId).finally(() => {
    inFlight.delete(queueId);
  });
  inFlight.set(queueId, p);
  return p;
}

/**
 * Cache-miss refresh used by RTCS queue event handlers. Behind
 * `featureRtcsSingleItemFetch`: fetches one queue via refreshQueue, falling
 * back to the full refreshQueues list when single-item returns null or throws.
 * Flag-off path matches pre-feature behavior exactly.
 */
export async function refreshQueueOnCacheMiss(queueId: string): Promise<void> {
  if (featureFlagModule.isEnabled(FeatureFlagKeys.featureRtcsSingleItemFetch)) {
    try {
      const fetched = await refreshQueue(queueId);

      if (fetched) return;

      logException(
        new Error(`refreshQueue returned null for queueId ${queueId}`),
        'rtcs:queue:refresh:single-item-miss',
      );
    } catch (err) {
      logException(err, 'rtcs:queue:refresh:single-item-failed');
    }
  }

  await refreshQueues();
}
